import { loadStripe, type Stripe, type StripeElements, type StripePaymentElement } from '@stripe/stripe-js';

// Retrieve Stripe Publishable Key from Vite env or window
export const STRIPE_PUBLISHABLE_KEY: string =
  (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY ||
  (import.meta as any).env?.STRIPE_PUBLISHABLE_KEY ||
  (typeof window !== 'undefined' && ((window as any).STRIPE_PUBLISHABLE_KEY || (window as any).VITE_STRIPE_PUBLISHABLE_KEY)) ||
  'pk_test_TYoo61EEYleQqNUrcqAcHA2t';

let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise || Promise.resolve(null);
};

export interface StripeMountResult {
  elements?: StripeElements;
  checkout?: any;
  paymentElement: StripePaymentElement | any;
  isCheckoutSession?: boolean;
}

export interface StripeCheckoutMountResult {
  checkout: any;
  paymentElement: any;
}

/**
 * Initialize Stripe Checkout Elements SDK and mount Payment Element
 * Recommended flow for Stripe Checkout Sessions API (`ui_mode: "elements"`)
 */
export async function mountStripeCheckoutElements(
  container: HTMLElement | string,
  clientSecret: string | Promise<string>,
  options?: any
): Promise<StripeCheckoutMountResult | null> {
  const stripe = await getStripe();
  if (!stripe) {
    console.warn('[StripeService] Stripe SDK failed to load or publishable key missing.');
    return null;
  }

  try {
    const target = typeof container === 'string' ? document.getElementById(container) : container;
    if (!target) {
      console.warn('[StripeService] Target container not found:', container);
      return null;
    }

    target.innerHTML = '';

    const clientSecretPromise = typeof clientSecret === 'string' ? Promise.resolve(clientSecret) : clientSecret;

    // Use initCheckoutElementsSdk for Checkout Sessions with Payment Element
    const checkout = await (stripe as any).initCheckoutElementsSdk({
      clientSecret: clientSecretPromise,
      ...options,
    });

    const paymentElement = checkout.createPaymentElement();
    paymentElement.mount(target);

    return {
      checkout,
      paymentElement,
    };
  } catch (err) {
    console.error('[StripeService] Failed to mount Stripe Checkout Payment Element:', err);
    return null;
  }
}

/**
 * Confirm payment using Checkout Elements SDK actions
 */
export async function confirmStripeCheckout(
  checkout: any
): Promise<{ success: boolean; error?: string }> {
  if (!checkout) {
    return { success: false, error: 'Checkout instance not initialized' };
  }

  try {
    const loadActionsResult = await checkout.loadActions();
    const actions = loadActionsResult.actions;
    const error = await actions.confirm();

    if (error) {
      return {
        success: false,
        error: error.message || 'Payment confirmation failed',
      };
    }

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'An error occurred during payment confirmation',
    };
  }
}

/**
 * Mount Stripe Payment Element into a DOM element
 * Supports Checkout Sessions (`ui_mode: "elements"`), Setup Intents, and Payment Intents
 */
export async function mountStripeElements(
  container: HTMLElement | string,
  clientSecret: string | Promise<string>
): Promise<StripeMountResult | null> {
  const stripe = await getStripe();
  if (!stripe) {
    console.warn('[StripeService] Stripe SDK failed to load or publishable key missing.');
    return null;
  }

  const rawSecret = typeof clientSecret === 'string' ? clientSecret : '';
  const isCheckoutSecret = rawSecret.startsWith('cs_');

  // If using Checkout Session or initCheckoutElementsSdk is preferred
  if (isCheckoutSecret && typeof (stripe as any).initCheckoutElementsSdk === 'function') {
    const checkoutResult = await mountStripeCheckoutElements(container, clientSecret);
    if (checkoutResult) {
      return {
        checkout: checkoutResult.checkout,
        paymentElement: checkoutResult.paymentElement,
        isCheckoutSession: true,
      };
    }
  }

  const target = typeof container === 'string' ? document.getElementById(container) : container;
  if (!target) {
    console.warn('[StripeService] Target container not found:', container);
    return null;
  }

  const resolvedSecret = typeof clientSecret === 'string' ? clientSecret : await clientSecret;

  if (!resolvedSecret || !resolvedSecret.includes('_secret_')) {
    console.warn('[StripeService] clientSecret is not a standard client_secret string:', resolvedSecret);
  }

  try {
    // Check if initCheckoutElementsSdk should be used
    if (typeof (stripe as any).initCheckoutElementsSdk === 'function' && resolvedSecret.startsWith('cs_')) {
      const checkoutResult = await mountStripeCheckoutElements(target, resolvedSecret);
      if (checkoutResult) {
        return {
          checkout: checkoutResult.checkout,
          paymentElement: checkoutResult.paymentElement,
          isCheckoutSession: true,
        };
      }
    }

    const elements = stripe.elements({
      clientSecret: resolvedSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#0284c7',
          colorBackground: '#020617',
          colorText: '#f8fafc',
          colorDanger: '#ef4444',
          fontFamily: 'Inter, system-ui, sans-serif',
          borderRadius: '16px',
          spacingUnit: '4px',
        },
        rules: {
          '.Tab': {
            border: '1px solid #1e293b',
            backgroundColor: '#0f172a',
          },
          '.Tab--selected': {
            border: '1px solid #0284c7',
            backgroundColor: '#0c4a6e',
          },
          '.Input': {
            border: '1px solid #1e293b',
            backgroundColor: '#020617',
          },
        },
      },
    });

    const paymentElement = elements.create('payment', {
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
      },
      paymentMethodOrder: ['card', 'customer_balance', 'konbini'],
    });

    target.innerHTML = '';
    paymentElement.mount(target);

    return {
      elements,
      paymentElement,
      isCheckoutSession: false,
    };
  } catch (err) {
    console.error('[StripeService] Failed to mount Stripe Payment Element:', err);
    return null;
  }
}

/**
 * Confirm Stripe Setup Intent or Checkout Session using Stripe.js SDK
 */
export async function confirmStripeSetupIntent(
  elementsOrMount: StripeElements | StripeMountResult | any,
  returnUrl?: string
): Promise<{ success: boolean; error?: string; setupIntent?: any }> {
  // If a Checkout instance is present, confirm via checkout actions
  if (elementsOrMount?.checkout || elementsOrMount?.isCheckoutSession) {
    const checkout = elementsOrMount.checkout || elementsOrMount;
    return await confirmStripeCheckout(checkout);
  }

  const stripe = await getStripe();
  if (!stripe) {
    return { success: false, error: 'Stripe SDK not initialized' };
  }

  const elements = elementsOrMount?.elements || elementsOrMount;

  const defaultReturnUrl =
    returnUrl ||
    (typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}?setup=success`
      : 'https://app.nextbasecloud.com/dashboard/billing?setup=success');

  try {
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: defaultReturnUrl,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      return {
        success: false,
        error: result.error.message || 'Payment method setup failed',
      };
    }

    return {
      success: true,
      setupIntent: result.setupIntent,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Failed to confirm setup',
    };
  }
}

/**
 * Universal payment or setup confirmation helper
 */
export async function confirmStripePaymentOrSetup(
  mountResult: StripeMountResult,
  returnUrl?: string
): Promise<{ success: boolean; error?: string; result?: any }> {
  if (mountResult.checkout) {
    return await confirmStripeCheckout(mountResult.checkout);
  }
  if (mountResult.elements) {
    return await confirmStripeSetupIntent(mountResult.elements, returnUrl);
  }
  return { success: false, error: 'No active Stripe mount elements or checkout instance' };
}

/**
 * Redirect to Stripe Checkout using Stripe.js SDK or checkout URL
 */
export async function redirectToStripeCheckout(
  sessionId?: string,
  checkoutUrl?: string
): Promise<void> {
  const stripe = await getStripe();

  if (sessionId && stripe) {
    try {
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.warn('[StripeService] redirectToCheckout warning:', error);
        if (checkoutUrl && typeof window !== 'undefined') {
          window.location.href = checkoutUrl;
        }
      }
      return;
    } catch (e) {
      console.warn('[StripeService] Error in stripe.redirectToCheckout:', e);
    }
  }

  if (checkoutUrl && typeof window !== 'undefined') {
    window.location.href = checkoutUrl;
  }
}
